import type { IPublicClientApplication } from '@azure/msal-browser';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from './msalConfig';

/**
 * Az API-réteg nem ismerheti az MSAL-t, a React-fát viszont nem tudja
 * elérni — ezért a `main.tsx` bootstrapkor ide adja be a példányt, és a
 * `getAccessToken()` innen szolgálja ki a fetch wrappert.
 *
 * Ez szándékosan modul-szintű állapot: egyetlen MSAL példány van, és a
 * kliensnek egyetlen helyen kell tudnia tokent kérni.
 */
let msalInstance: IPublicClientApplication | null = null;

export function setMsalInstance(instance: IPublicClientApplication): void {
  msalInstance = instance;
}

export class NotAuthenticatedError extends Error {
  constructor() {
    super('Nincs bejelentkezett felhasználó.');
    this.name = 'NotAuthenticatedError';
  }
}

/**
 * Érvényes access token a `access_as_user` scope-ra.
 *
 * Csendes megújítást próbál. Ha az MSAL azt mondja, hogy interakció kell
 * (lejárt refresh token, visszavont consent, megváltozott jelszó), akkor
 * átirányít a bejelentkezésre — NEM dobunk hibát a hívónak, mert egy
 * "Nincs bejelentkezve" toast ilyenkor félrevezető: nem a művelet bukott
 * el, hanem a munkamenet járt le.
 */
export async function getAccessToken(): Promise<string> {
  if (!msalInstance) throw new NotAuthenticatedError();

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) throw new NotAuthenticatedError();

  try {
    const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
      // Az átirányítás elnavigál az oldalról; ez a sor gyakorlatilag nem fut le.
      throw new NotAuthenticatedError();
    }
    throw error;
  }
}
